import type { NextPageContext } from "next";
import { env } from "@monta/env";
import type {
  TUnknownObject,
  TUnknownArray,
  TCookieObject,
  TCookieValue,
  TOptions,
} from "./types";

const cookieOptions = [
  "maxAge",
  "domain",
  "path",
  "expires",
  "httpOnly",
  "secure",
];

function isObjectOrArray(obj: TUnknownObject | TUnknownArray) {
  const type = Object.prototype.toString.call(obj);
  return type === "[object Object]" || type === "[object Array]";
}

function encodeValue(val: unknown) {
  if (typeof val === "string") return val;
  return JSON.stringify(val);
}

function decodeValue(val: unknown) {
  const type = Object.prototype.toString.call(val);
  if (type === "[object Object]") {
    return val;
  }
  if (type === "undefined") {
    return val;
  }
  try {
    return JSON.parse(val as string);
  } catch (error) {
    return val;
  }
}

function decodeCookie(cookie: string): TCookieObject {
  cookie = decodeURI(cookie);
  const arr = cookie.split("; ");
  const obj: TCookieObject = {};
  function getCookieOptions(i: number): TUnknownObject {
    const cookie = arr[i].split("=");
    const key = cookie[0];
    const val = cookie[1];
    const nextCookie = i + 1 < arr.length ? arr[i + 1].split("=") : null;
    if (cookieOptions.find((i) => kebabize(i) === key)) {
      if (
        nextCookie &&
        cookieOptions.find((i) => kebabize(i) === nextCookie[0])
      ) {
        return { [key]: val, ...getCookieOptions(i + 1) };
      } else {
        return { [key]: val };
      }
    } else {
      return {};
    }
  }
  for (let i = 0; i < arr.length; i++) {
    const splited = arr[i].split("=");
    if (splited.length > 1) {
      const key = splited[0];
      const value = splited[1];
      const options = getCookieOptions(i);
      // if (options) {
      obj[key] = {
        _value: value,
        _options: options,
      };
      // } else obj[key] = value;
    }
  }
  return obj;
}

function encodeCookie(cookie: TCookieObject) {
  const arr: string[] = [];
  for (const key in cookie) {
    const value = cookie[key];
    if (typeof value === "object") {
      value._value = encodeURIComponent(value._value);
      for (const option in value._options) {
        if (cookieOptions.includes(option)) {
          const optionValue = value._options[option];
          value._value += `; ${kebabize(option)}=${optionValue}`;
        }
      }
      arr.push(`${key}=${value._value}`);
    } else {
      arr.push(`${key}=${encodeURIComponent(value)}`);
    }
  }
  return arr.join("; ");
}

function kebabize(str: string) {
  const arr: string[] = [];
  let buffer = "";
  for (let i = 0; i < str.length; i++) {
    const letter = str[i];
    if (letter === letter.toUpperCase()) {
      arr.push(buffer);
      buffer = letter;
    } else buffer += letter.toLowerCase();
  }
  arr.push(buffer);
  return arr.join("-");
}

export class Storage {
  ctx: NextPageContext | undefined;
  options: TOptions;
  constructor(ctx: NextPageContext | undefined, options: TOptions) {
    this.ctx = ctx;
    this.options = options;
  }
  setUniversal(key: string, value: TCookieValue) {
    this.setCookie(key, value);
    this.setLocalStorage(key, value);
    return value;
  }

  getUniversal(key: string) {
    let value = this.getCookie(key);
    if (!value) value = this.getLocalStorage(key);
    return value;
  }

  removeUniversal(key: string) {
    this.removeLocalStorage(key);
    this.removeCookie(key);
  }

  setLocalStorage(key: string, value: TCookieValue) {
    if (typeof localStorage === "undefined" || !this.options.localStorage) {
      return;
    }

    const _key = this.options.localStorage.prefix + key;

    if (isObjectOrArray(value as TUnknownArray | TUnknownObject)) {
      localStorage.setItem(_key, JSON.stringify(value));
    } else {
      localStorage.setItem(_key, value as string);
    }

    return value;
  }

  getLocalStorage<T = unknown>(key: string): T | undefined {
    if (typeof localStorage === "undefined" || !this.options.localStorage) {
      return;
    }

    const _key = this.options.localStorage.prefix + key;

    const value = localStorage.getItem(_key);

    return decodeValue(value);
  }

  removeLocalStorage(key: string) {
    if (typeof localStorage === "undefined" || !this.options.localStorage) {
      return;
    }
    const _key = this.options.localStorage.prefix + key;
    localStorage.removeItem(_key);
  }
  clearAllLocalStorage() {
    const keys: string[] = Object.keys(localStorage);
    const prefix = this.options.localStorage?.prefix;
    if (keys.length > 0) {
      keys.map((key) => {
        if (typeof window !== "undefined") {
          localStorage.removeItem(`${prefix}${key}`);
        }
      });
    }
  }
  getCookies() {
    const cookieStr =
      typeof window !== "undefined"
        ? document.cookie
        : this.ctx?.req?.headers?.cookie;

    return decodeCookie(cookieStr || "") || {};
  }

  setCookie(
    key: string | string[],
    value: TCookieValue | TCookieValue[],
    options = {},
  ) {
    if (!this.options.cookie) {
      return;
    }
    const keys = Array.isArray(key) ? key : [key];
    const values = Array.isArray(value) ? value : [value];

    const cookies: TCookieObject[] = [];

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const value = values[i];
      const _key = this.options.cookie.prefix + key;
      const _options = Object.assign({}, options);
      const _value = encodeValue(value);
      const cookie: TCookieObject = {};
      cookie[_key] = { _value, _options };
      cookies.push(cookie);
    }

    if (typeof window !== "undefined") {
      cookies.forEach((cookie) => {
        const serializedCookie = encodeCookie(cookie);
        document.cookie = serializedCookie;
      });
    } else if (typeof window === "undefined" && this.ctx?.res) {
      this.ctx.res.setHeader("Set-Cookie", cookies.map(encodeCookie));
    }

    return value;
  }

  getCookie<T = any>(key: string): T | undefined {
    if (
      !this.options.cookie ||
      (typeof window === "undefined" && !this.ctx?.req)
    ) {
      return;
    }

    const _key = this.options.cookie.prefix + key;

    const cookies = this.getCookies();

    const value = cookies[_key]
      ? decodeURIComponent(cookies[_key]._value)
      : undefined;

    return decodeValue(value);
  }

  removeCookie(key: string) {
    const cookieString = `${key}=;path=/;domain=${env.COOKIE_DOMAIN};expires=Thu, 01 Jan 1970 00:00:01 GMT`;
    if (typeof window !== "undefined") {
      document.cookie = cookieString;
    } else if (typeof window === "undefined" && this.ctx?.res) {
      this.ctx.res.setHeader("Set-Cookie", cookieString);
    }
  }
}

export default function createStorage(ctx?: NextPageContext) {
  return new Storage(ctx, {
    cookie: { prefix: "" },
    localStorage: { prefix: "" },
  });
}
